export const IN_ACTIVE = "inactive";
export const TRIAL = "trial";
export const ACTIVE = "active";
export const EXPIRED = "expired";

export const gradingToolButtonLabelMapper = {
  [IN_ACTIVE]: "Limited Access",
  [TRIAL]: "Select",
  [ACTIVE]: "Select",
  [EXPIRED]: "Get premium",
};

/*
  Token-aware color mappers.
  We store CSS variable references (strings) instead of raw hex so theme switching works automatically.
  Disabled/inactive states fall back to a neutral border/text token. If design later adds --color-disabled, update here.
*/
export const gradingToolColorMapper = {
  [IN_ACTIVE]: 'var(--color-border-strong)',
  [EXPIRED]: 'var(--color-border-strong)',
  [TRIAL]: 'var(--color-primary)',
  [ACTIVE]: 'var(--color-primary)',
};

export const gradingToolButtonColorMapper = {
  [IN_ACTIVE]: 'var(--color-border-strong)',
  [EXPIRED]: 'var(--color-border-strong)',
  [TRIAL]: 'var(--color-accent)',
  [ACTIVE]: 'var(--color-accent)',
};

/* Optional helper: returns style obj for a tool status (border + button bg)
   Usage example (if desired):
      const { borderColor, buttonBg } = getGradingToolColors(status);
*/
export const getGradingToolColors = (status) => ({
  borderColor: gradingToolColorMapper[status] || 'var(--color-border)',
  buttonBg: gradingToolButtonColorMapper[status] || 'var(--color-primary)',
});


export const SurveyMode =
  process.env.REACT_APP_SURVEY_MODE === "1" ? true : false;

let apiUrl;
let frontendUrl;
let client_id;

switch (process.env.REACT_APP_ENV) {
  case "prod":
    apiUrl = process.env.REACT_APP_PROD_BACKEND_URL;
    frontendUrl = process.env.REACT_APP_PROD_FRONTEND_URL;
    client_id = process.env.REACT_APP_GOOGLE_PROD_CLIENT_ID;
    break;
  case "dev":
    apiUrl = process.env.REACT_APP_DEV_BACKEND_URL;
    frontendUrl = process.env.REACT_APP_DEV_FRONTEND_URL;
    client_id = process.env.REACT_APP_GOOGLE_DEV_CLIENT_ID;
    break;
  case "local":
  default:
    apiUrl = process.env.REACT_APP_LOCAL_BACKEND_URL;
    frontendUrl = process.env.REACT_APP_LOCAL_FRONTEND_URL;
    client_id = process.env.REACT_APP_GOOGLE_DEV_CLIENT_ID;
    break;
}
export const API_URL = apiUrl;
export const FRONTEND_URL = frontendUrl;
export const CLIENT_ID = client_id;

// Utility function to fix verification URLs for local development
export const getLocalVerificationUrl = (verificationUrl) => {
  if (process.env.REACT_APP_ENV === 'local' && verificationUrl) {
    // Extract credential ID from the backend verification URL
    // Backend URL format: http://localhost:8000/api/badges/verify/{credential_id}/
    const credentialIdMatch = verificationUrl.match(/\/verify\/([^/]+)\/?$/);
    if (credentialIdMatch) {
      const credentialId = credentialIdMatch[1];
      // Get frontend URL from environment  
      const localFrontendUrl = process.env.REACT_APP_LOCAL_FRONTEND_URL;
      if (localFrontendUrl) {
        // Return frontend verification URL: http://localhost:3000/badges/verify/{credential_id}
        return `${localFrontendUrl.replace(/\/$/, '')}/badges/verify/${credentialId}`;
      }
    }
  }
  return verificationUrl;
};

export const topicOptions = [
  "The unlucky gardener",
  "An ill-fated camping trip",
  "Smurfs in space",
  "A gram of sugar doing the tango",
  "Operation: Alien Rescue",
  "Abraham Lincoln Emancipation Proclamation",
  "Against the tide: An Olympic swimmer's journey through turbulence",
  "Leonardo da Vinci",
  "Joan of Arc",
  "Marie Curie and the discovery of radium",
  "Why do volcanoes erupt?",
  "The impact of climate change on marine biodiversity in coral reefs",
  "The role of CRISPR-Cas9 in targeted genome editing for therapeutic applications",
  "Advancements in quantum computing and their complications for cybersecurity",
  "Harnessing Artificial Intelligence for drug discovery in the fight against antimicrobial resistance",
  "The fall of the Berlin Wall",
  "Hiroshima atomic bombing",
  "The first moon landing",
  "The stock market crash of 1929",
  "The discovery of the tomb of Tutankhamum",
  "The storming of the Bastille",
  "Digital media",
  "Innovation",
  "Science",
  "Space exploration",
  "Our planet",
  "Sports",
  "History",
  "Social issues",
  "Philanthropy",
  "Activism",
  "Travel",
  "Philosophy",
];

export const genreOptions = [
  "Informational Text",
  "Historical Narrative",
  "Scientific Article",
  "Newspaper Article",
  "Newspaper Article (op-ed)",
  "Personal Narrative",
  "Realistic Fiction",
  "Historical Fiction",
  "Fantasy",
  "Science Fiction",
  "Mystery",
  "Satire",
  "Excerpt from a Scholarly Paper",
  "Excerpt from a Scientific Article",
];

export const genreOptionsAssessment = [
  "None selected",  
  "Nonfiction text (eg: textbook chapter, primary source, etc)",
  "Fictional text (eg: reading passage, book chapter, poem, etc)",
  "List of terms (eg: lesson plan, terms, syllabus, notes, etc)",
];

export const gradeOptions = [
  "Kindergarten",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
  "Other",
];

export const teacherGradeOptions = [
  "None",
  "Kindergarten",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
  "Other",
];

export const teacherSubjectOptions = [
  "None",
  "English",
  "History",
  "Social Studies",
  "World Languages",
  "Religion or Theology",
  "Science",
  "Math",
  "Other",
];

export const referralOptions = [
  "Facebook",
  "LinkedIn",
  "Twitter",
  "Search Engine (Google, Bing, etc.)",
  "ClassGenius Website",
  "Colleague or Friend",
  "Conference",
  "Industry Partnership (e.g., City Year)",
  "Other",
];

export const responsibilitiesOptions = [
  "Educator",
  "Principal",
  "Administrator",
  "Learning Specialist",
  "IT Specialist",
  "Student",
  "Parent or Guardian",
  "Other",
];

export const languages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Dutch",
  "Mandarin pinyin",
  "Mandarin simplified Chinese",
];

export const showText = [
  { name: "No, the test is closed book", value: "Closed book" },
  { name: "Yes, the test is open book", value: "Open book" },
];

export const gradeOptionsTools = [
  { name: "None selected", value: "" },
  "Kindergarten",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
];

export const readingComprehensionLength = [
  { name: "None selected", value: "" },
  "Less than 100 words",
  "100 to 250 words",
  "250 to 500 words",
  "500 to 750 words",
  "750 to 1,000 words",
  "1,000 to 1,500 words"
]

export const subjectsOptions = [
  { name: "None selected", value: "" },
  "History",
  "English",
  "Science",
  "Math",
  "ESL",
  "World languages",
  "Social studies",
  "Other",
];
export const difficultyOptionsUpdated = [
  "Easy",
  "Medium",
  "Hard",
];

export const releaseGradeOptions = [
  {
    label: "Later, after manual review",
    value: "Later, after manual review",
  },
  {
    label: "Immediately after each submission",
    value: "Immediately after each submission",
  },
];

export const exportFormatOptions = [
  {
    label: "Google Docs",
    value: "Google Docs",
  },
  {
    label: "Google Forms",
    value: "Google Forms",
  },
  {
    value: "Spreadsheet compatible with Kahoot import",
    label: "Spreadsheet compatible with Kahoot import"
  },
  {
    value: "Spreadsheet compatible with Blooket import",
    label: "Spreadsheet compatible with Blooket import"
  }

];

export const studentCanSeeOptions = [
  {
    label: "Missed question",
    value: "Missed question",
  },
  {
    label: "Correct answers",
    value: "Correct answers",
  },
  {
    label: "Point values",
    value: "Point values",
  },
];

export const CARDS_DATA = [
  {
    subtitle: "Ideal for skill-building assignments, quizzes, and assessments (open or closed-book)",
    bulletPoints: [
      "Create questions in your preferred language.",
      "Choose between open or closed-book assessments.",
      "Multiple-choice or short answer? We support both.",
      "Adjustable to grade level (5-12), plus three different difficulty levels within each grade.",
      "Export in ready-to-use format to Google Docs, Google Forms, Kahoot, or Blooket.",
    ],
  },
  {
    subtitle:
      "Ideal for practicing reading fluency, comprehension, or to help students study a topic",
    bulletPoints: [
      "Create an assignment in your preferred language.",
      "Choose from a wide variety of styles-- fiction and nonfiction.",
      "Multiple-choice or short answer? We support both.",
      "Adjustable to grade level (5-12), plus three different difficulty levels within each grade.",
      "Export in ready-to-use format to Google Docs, Google Forms, Kahoot, or Blooket.",
    ],
  },
  {
    subtitle:
      "Get help grading student papers, with or without a rubric",
    bulletPoints: [
      "Bullet pt 1.",
      "Bullet pt 2.",
    ],
  },
  {
    subtitle:
      "Ideal for generating reading worksheets for your school's ELA curriculum",
    bulletPoints: [
      "Generate a worksheet",
    ],
  },
  {
    subtitle:
      "Ideal for adapting content to different reading levels and ensuring text accessibility for all students",
    bulletPoints: [
      "Adjust text complexity for to students' abilities",
    ],
  },
];
export const MATCHING_TYPES = 
  {
    1: "Spanish/English Matching",
    2: "Spanish/English Image Matching",
    3: "English/Image Matching",
    4: "Matching Cutout",
    5: "Tongan/English Matching",
    6: "Tongan/English Image Matching",
    7: "Practice Reading",
    8: "Fill in the Blank",
  };
